/* Email capture: teaser and panel triggered on real engagement. */

const SESSION_KEY = 'dyncel_ec_session_v1';
const STATE_KEY = 'dyncel_ec_v1';
// Settle delay after `load` before anything appears (teaser or auto-opened panel).
// Triggers keep arming during load; only the visual appearance waits.
const APPEARANCE_SETTLE_DELAY_MS = 800;
// Fail-safe: `load` may never fire (a slow or blocked third-party resource can leave the page
// in readyState 'interactive'). This cap only guarantees an appearance in that case.
const PAGE_SETTLED_FALLBACK_MS = 5000;

class EmailCaptureForm extends HTMLElement {
  connectedCallback() {
    const d = this.dataset;
    this.config = {
      key: d.key || '',
      list: d.list || '',
      template: d.template || '',
      productHandle: d.productHandle || '',
      ageRequired: d.ageRequired === 'true',
      autoOpen: d.autoOpen === 'true',
      triggerExit: d.triggerExit === 'true',
      triggerEscalation: d.triggerEscalation === 'true',
      triggerCart: d.triggerCart === 'true',
      productViews: parseInt(d.productViews, 10) || 2,
      dwellSeconds: parseInt(d.dwellSeconds, 10) || 25,
      scrollPercent: parseInt(d.scrollPercent, 10) || 60,
      frequencyDays: parseInt(d.frequencyDays, 10) || 30,
    };
    this.session = this.readJson(sessionStorage, SESSION_KEY)
      || { p: 0, e: [], sc: false, cartReturnDone: false, ao: false, pc: false, esc: false };
    this.state = this.readJson(localStorage, STATE_KEY) || { cap: null, sub: null };
    this.cartCount = 0;
    // Resolved at load + settle delay; awaited by showTeaser() and the auto path of openPanel()
    // before they set visibility attributes. Created before start() so it is ready for the
    // first evaluate('pageload').
    this.pageSettled = new Promise((resolve) => {
      let settled = false;
      const settle = () => { if (settled) return; settled = true; resolve(); };
      if (document.readyState === 'complete') {
        setTimeout(settle, APPEARANCE_SETTLE_DELAY_MS);
      } else {
        // The fallback only covers the case where `load` never fires. If `load` already fired, the
        // settle at load + delay is scheduled and must not be short-circuited.
        let loadFired = false;
        window.addEventListener('load', () => {
          loadFired = true;
          setTimeout(settle, APPEARANCE_SETTLE_DELAY_MS);
        }, { once: true });
        setTimeout(() => { if (!loadFired) settle(); }, PAGE_SETTLED_FALLBACK_MS);
      }
    });
    if (this.state.sub) return;
    this.session.p += 1;
    this.saveSession();
    this.start();
    // Re-check bannerVisible() once the page settles, not only at parse time: the consent banner
    // usually renders shortly after parse, after start() but before pageSettled resolves. A
    // banner appearing in that window would otherwise suppress the teaser and the auto-open for
    // good, since nothing else re-triggers evaluate(). This arms the watcher lazily only if the
    // banner is visible then; no interval is created otherwise.
    this.pageSettled.then(() => this.armBannerWatch());
  }

  start() {
    this.teaser = this.querySelector('.email-capture__teaser');
    this.panel = this.querySelector('.email-capture__panel');
    this.scrim = this.querySelector('.email-capture__scrim');
    this.teaser.addEventListener('click', () => {
      this.emit('teaser_click');
      this.openPanel('teaser_click');
    });
    this.querySelector('.email-capture__close').addEventListener('click', () => this.closePanel());
    if (this.scrim) {
      this.scrim.addEventListener('click', () => this.closePanel());
    }
    this.form = this.querySelector('.email-capture__form');
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel.getAttribute('data-open') === 'true') this.closePanel();
    });
    this.trackEngagement();
    this.evaluate('pageload');
    // The exit-intent listener also arms when only the escalation is enabled.
    if ((this.config.triggerExit || this.config.triggerEscalation) && this.isDesktopPointer()) {
      document.addEventListener('mouseout', (e) => {
        if (e.clientY <= 0 && !e.relatedTarget) this.onExitIntent();
      });
    }
    this.armBannerWatch();
    if (this.config.triggerCart) {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          this.cartCount = cart.item_count || 0;
          this.maybeCartReturn();
        })
        .catch(() => {});
      document.addEventListener('cart:update', (e) => {
        const count = e.detail?.data?.itemCount;
        if (typeof count === 'number') {
          this.cartCount = count;
        } else {
          fetch('/cart.js').then((r) => r.json()).then((c) => { this.cartCount = c.item_count || 0; }).catch(() => {});
        }
      });
    }
  }

  readJson(storage, key) {
    try { return JSON.parse(storage.getItem(key)); } catch { return null; }
  }

  saveSession() {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.session)); } catch {}
  }

  saveState() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(this.state)); } catch {}
  }

  emit(action, detail = {}) {
    document.dispatchEvent(new CustomEvent(`email-capture:${action}`, { detail, bubbles: true }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'email_capture', ec_action: action, ...detail });
    // Also published on the Shopify analytics bus for the custom pixel. No network request here.
    const bus = globalThis.Shopify && globalThis.Shopify.analytics;
    if (bus && typeof bus.publish === 'function') {
      try { bus.publish('dyncel_email_capture', { action, ...detail }); } catch {}
    }
  }

  // Arms the 500ms banner-visibility poll only if the banner is visible right now and no watch
  // is running. Called at parse time (start()) and once more after pageSettled resolves, which
  // catches a banner rendered between those two moments. Idempotent.
  armBannerWatch() {
    if (this.bannerWatch) return;
    if (!this.bannerVisible()) return;
    this.bannerWatch = setInterval(() => {
      if (!this.bannerVisible()) {
        clearInterval(this.bannerWatch);
        this.bannerWatch = null;
        this.evaluate('banner_resolved');
      }
    }, 500);
  }

  bannerVisible() {
    const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
    if (!banner) return false;
    // Check if element has size (accounts for both regular and fixed positioning)
    const rect = banner.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0;
  }

  isDesktopPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  trackEngagement() {
    if (this.config.template === 'product' && this.config.productHandle) {
      if (!this.session.e.includes(this.config.productHandle)) {
        this.startDwellTimer();
        this.scrollHandler = () => {
          const doc = document.documentElement;
          const progress = (window.scrollY + window.innerHeight) / doc.scrollHeight * 100;
          if (progress >= this.config.scrollPercent) this.markEngaged('scroll');
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        document.addEventListener('cart:update', () => this.markEngaged('interaction'), { once: true });
        document.addEventListener('toggle', (e) => {
          if (e.target instanceof HTMLDetailsElement) this.markEngaged('interaction');
        }, { capture: true, once: true });
      }
    } else {
      this.scrollHandler = () => {
        const doc = document.documentElement;
        const progress = (window.scrollY + window.innerHeight) / doc.scrollHeight * 100;
        if (progress >= 50 && !this.session.sc) {
          this.session.sc = true;
          this.saveSession();
          this.evaluate('scroll');
        }
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  startDwellTimer() {
    let elapsed = 0;
    this.dwellInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        elapsed += 1;
        if (elapsed >= this.config.dwellSeconds) this.markEngaged('dwell');
      }
    }, 1000);
  }

  markEngaged(source) {
    if (this.session.e.includes(this.config.productHandle)) return;
    clearInterval(this.dwellInterval);
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    this.session.e.push(this.config.productHandle);
    this.saveSession();
    this.emit('product_engaged', { source });
    this.evaluate('engaged');
  }

  teaserEligible() {
    return !this.session.tc
      && !this.bannerVisible()
      && (this.session.p >= 2 || this.session.e.length >= 1 || this.session.sc);
  }

  capActive() {
    return !!this.state.cap && (Date.now() - this.state.cap) < this.config.frequencyDays * 864e5;
  }

  canAutoOpen(reason) {
    if (!this.config.autoOpen) return false;
    if (this.capActive()) return false;
    if (this.bannerVisible()) return false;
    if (this.panel.getAttribute('data-open') === 'true') return false;
    const firstPageOk = this.session.p >= 2 || reason === 'exit_intent' || reason === 'cart_exit';
    return firstPageOk;
  }

  evaluate(moment) {
    if (this.teaserEligible()) this.showTeaser();
    if (this.session.e.length >= this.config.productViews && this.canAutoOpen('product_views')) {
      this.openPanel('product_views');
    }
  }

  onExitIntent() {
    if (this.exitFired) return;
    this.exitFired = true;
    // Escalation to a centred modal when the auto-open was ignored, checked first. Does not use
    // canAutoOpen(): the cap was already set by that same auto-open in this session, so checking
    // it here would always block the escalation. Own guards: never after a voluntary close
    // (session.pc), never when subscribed (state.sub), never while the cookie banner is visible,
    // never twice in the same session (session.esc).
    if (
      this.config.triggerEscalation
      && this.session.ao && !this.session.pc && !this.session.esc
      && !this.state.sub && !this.bannerVisible()
    ) {
      this.openPanel('exit_escalation');
      return;
    }
    if (this.config.triggerExit && this.session.e.length >= 1 && this.canAutoOpen('exit_intent')) {
      this.openPanel('exit_intent');
    } else if (this.cartCount > 0 && this.config.triggerCart && this.canAutoOpen('cart_exit')) {
      this.openPanel('cart_exit');
    }
  }

  maybeCartReturn() {
    if (this.isDesktopPointer()) return;
    if (this.session.cartReturnDone) return;
    this.session.cartReturnDone = true;
    this.saveSession();
    if (this.session.p !== 1 || this.cartCount === 0) return;
    const ref = document.referrer;
    const external = ref && new URL(ref).origin !== location.origin;
    if (external) return;
    if (this.canAutoOpen('cart_exit')) this.openPanel('cart_exit');
  }

  // Async: waits for the page to settle before setting data-visible. The trigger stays armed
  // immediately; only the visual appearance is deferred. Guards are re-checked after the wait,
  // the state may have changed meanwhile (teaser closed, banner shown).
  async showTeaser() {
    if (!this.teaser) return;
    if (this.teaser.getAttribute('data-visible') === 'true' || this._teaserPending) return;
    this._teaserPending = true;
    await this.pageSettled;
    this._teaserPending = false;
    if (this.teaser.getAttribute('data-visible') === 'true') return;
    if (!this.teaserEligible()) return;
    this.teaser.setAttribute('data-visible', 'true');
    this.emit('teaser_shown');
  }

  hideTeaser(closedByUser = false) {
    if (!this.teaser) return;
    this.teaser.removeAttribute('data-visible');
    if (closedByUser) {
      this.session.tc = true;
      this.saveSession();
      this.emit('teaser_closed');
    }
  }

  // The auto path (reason !== 'teaser_click') waits for pageSettled then re-checks its guards:
  // canAutoOpen(reason) for standard reasons, the escalation guards for 'exit_escalation'
  // (see onExitIntent). The manual click never awaits before these lines: immediate opening.
  async openPanel(reason) {
    if (this.panel.getAttribute('data-open') === 'true') return;
    const isAuto = reason !== 'teaser_click';
    const isEscalation = reason === 'exit_escalation';
    if (isAuto) {
      await this.pageSettled;
      if (this.panel.getAttribute('data-open') === 'true') return;
      if (isEscalation) {
        if (this.state.sub || this.session.pc || this.session.esc || this.bannerVisible()) return;
      } else if (!this.canAutoOpen(reason)) {
        return;
      }
    }
    this.autoOpened = isAuto;
    if (isAuto) {
      // The cap is set as soon as an auto-open happens, seen or not: one auto-open per
      // frequencyDays window.
      this.state.cap = Date.now();
      this.saveState();
      this.session.ao = true;
      if (isEscalation) this.session.esc = true;
      this.saveSession();
    }
    if (isEscalation) {
      this.panel.setAttribute('data-mode', 'modal');
      this.panel.setAttribute('aria-modal', 'true');
      if (this.scrim) this.scrim.setAttribute('data-open', 'true');
      // Arms the focus trap (handleModalTabKey) for the duration of the modal mode; removed
      // symmetrically in closePanel().
      this._modalTabHandler = (e) => this.handleModalTabKey(e);
      document.addEventListener('keydown', this._modalTabHandler);
    }
    this.panel.setAttribute('data-open', 'true');
    this.panel.setAttribute('data-reason', reason);
    this.teaser.setAttribute('aria-expanded', 'true');
    // Focus moves to the email field only on a direct gesture (teaser click) or on the modal
    // escalation, where aria-modal requires an initial focus and the focus trap below relies
    // on it. Anchored auto-opens never interrupt what the visitor is doing.
    if (reason === 'teaser_click' || isEscalation) {
      this.querySelector('#EmailCaptureEmail').focus();
    }
    this.emit('panel_open', { reason });
  }

  // Minimal focus trap, active only in modal mode. Tab/Shift+Tab cycle through the focusable
  // elements of the panel instead of leaving to the background, which otherwise stays
  // reachable by keyboard as no `inert` polyfill is applied.
  handleModalTabKey(e) {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(this.panel.querySelectorAll('input, button, a[href]'))
      .filter((el) => !el.disabled && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  closePanel() {
    this.panel.removeAttribute('data-open');
    this.panel.removeAttribute('data-mode');
    this.panel.removeAttribute('aria-modal');
    if (this.scrim) this.scrim.removeAttribute('data-open');
    // Removes the focus trap set when entering modal mode (see openPanel), whichever way the
    // panel closes (close button, Escape, scrim click).
    if (this._modalTabHandler) {
      document.removeEventListener('keydown', this._modalTabHandler);
      this._modalTabHandler = null;
    }
    this.teaser.setAttribute('aria-expanded', 'false');
    // A voluntary close (close button, Escape, scrim click) means "no thanks": blocks any
    // further escalation in the session.
    this.session.pc = true;
    this.saveSession();
    this.state.cap = Date.now();
    this.saveState();
    this.emit('panel_dismiss');
    if (this.teaser.getAttribute('data-visible') === 'true') this.teaser.focus();
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.form.reportValidity()) return;
    const email = this.form.elements.email.value.trim();
    const reason = this.panel.getAttribute('data-reason') || 'manual';
    const submitBtn = this.querySelector('.email-capture__submit');
    const message = this.querySelector('.email-capture__message');
    submitBtn.disabled = true;
    this.emit('submit', { reason });
    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.config.key)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2025-04-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: `theme:${reason}`,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: this.config.list } } },
            },
          }),
        }
      );
      if (!res.ok && res.status !== 202) throw new Error(String(res.status));
      this.state.sub = Date.now();
      this.saveState();
      this.emit('submit_success', { reason });
      try { window.klaviyo?.identify({ email }); } catch {}
      message.textContent = this.panel.dataset.successText;
      message.setAttribute('data-tone', 'success');
      this.form.querySelectorAll('input, button').forEach((el) => { el.disabled = true; });
      this.hideTeaser();
    } catch (err) {
      this.emit('submit_error', { status: err.message });
      message.textContent = this.panel.dataset.errorText;
      message.setAttribute('data-tone', 'error');
      submitBtn.disabled = false;
    }
  }
}

if (!customElements.get('email-capture-form')) {
  customElements.define('email-capture-form', EmailCaptureForm);
}
